import React, { useState, useEffect } from "react"
import { Box } from "@mui/material"

import ProductionStepExecutionsTile from "./ProductionStepExecutionsTile"
import ProductionStepExecutionsFilters from "./ProductionStepExecutionsFilters"
import { COLORS } from "../../utils"
import { getTileChips } from "../../utils/tiles"
import NoPSEs from "./NoPSEs"
import { PSES_SIDEBAR_MENUS_ENUM } from "../../utils/productionStepExecution"

const FILTER_DEFAULTS = {
  searchText: "",
  machine: [],
  transformationType: [],
  specificity: [],
  status: "TODO",
}

const filterPsEbyText = (PSE, searchText) => {
  if (!searchText) return true
  const textToSearch = `${PSE.PSEName}${PSE.recipeName}${PSE.sectionName}${PSE.uniqueCode || ""}`.toLowerCase()
  const searchLower = searchText.toLowerCase()
  return textToSearch.includes(searchLower)
}

const filterPsEbySelects = (PSE, filters, options = {}) => {
  // Machine filter
  if (filters.machine.length > 0) {
    const machineNames = PSE.machinesBatch?.map(mb => mb.machineName) || []
    const machineMatch = filters.machine.some(filterMachine => machineNames.includes(filterMachine))
    if (!machineMatch) return false
  }
  // Transformation Type filter
  if (filters.transformationType.length > 0) {
    const transformationType = PSE.productionStepSnapshot?.transformation
    if (!filters.transformationType.includes(transformationType)) return false
  }

  if (filters.specificity.length > 0) {
    const tileVisuals = PSE.productionStepSnapshot ? getTileChips(PSE) : []
    if (!filters.specificity.some(specificity => tileVisuals.some(visual => visual.label === specificity))) return false
  }

  if (!options.withoutStatus && filters.status) {
    if (PSE.status !== filters.status) return false
  }
  return true
}

const groupPseByStatus = (PSETiles) => PSETiles.reduce((acc, tile) => {
  if (!acc[tile.status]) {
    acc[tile.status] = []
  }
  acc[tile.status].push(tile)
  return acc
}, {})

const ProductionStepExecutionsTable = ({
  PSETiles,
  renderPrint,
  onStatusChange,
  productionDay,
  onTileClick,
  selectedMenu,
  isSidebarExpanded,
  // from store
  statusNotificationCounts
}) => {

  const [filters, setFilters] = useState(FILTER_DEFAULTS)
  const [filteredStatusCount, setFilteredStatusCount] = useState(groupPseByStatus(PSETiles))
  const [filteredTiles, setFilteredTiles] = useState(PSETiles)
  const [statusCounts, setStatusCounts] = useState({})

  useEffect(() => {
    // Dynamic status counts based on filtered PSEs (from query)
    if (statusNotificationCounts) {
      setStatusCounts(statusNotificationCounts)
      return
    }

    // Otherwise, compute counts from filtered PSEs array
    const counts = Object.keys(filteredStatusCount).reduce((acc, key) => {
      acc[key] = acc[key] ?? 0
      acc[key] += filteredStatusCount[key].length
      return acc
    }, {})
    setStatusCounts(counts)
  }, [statusNotificationCounts, filteredStatusCount])

  useEffect(() => {
    const withoutStatusFilter = (selectedMenu === PSES_SIDEBAR_MENUS_ENUM.TASTING
      || filters.status === "DONE" //TO_TEST can be viewed with done steps
    )
    const pseWithoutStatus = PSETiles
      .filter((tile) => filterPsEbyText(tile, filters.searchText))
      .filter((tile) => filterPsEbySelects(tile, filters, { withoutStatus: withoutStatusFilter }))

    setFilteredStatusCount(groupPseByStatus(pseWithoutStatus))

    const data = PSETiles
      .filter((tile) => filterPsEbyText(tile, filters.searchText))
      .filter((tile) => filterPsEbySelects(tile, filters, { withoutStatus: withoutStatusFilter }))

    setFilteredTiles(data)
  }, [PSETiles, filters, selectedMenu])


  useEffect(() => {
    setFilters(FILTER_DEFAULTS)
  }, [selectedMenu])

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters })
  }

  const handleTileClick = (productionStepExecution) => {
    if (onTileClick) {
      onTileClick(productionStepExecution)
    }
  }

  return (
    <>
      <ProductionStepExecutionsFilters
        PSETiles={PSETiles}
        filters={filters}
        onFilterChange={handleFilterChange}
        selectedStatus={filters.status}
        onStatusChange={onStatusChange}
        statusCounts={statusCounts}
        selectedMenu={selectedMenu}
        isSidebarExpanded={isSidebarExpanded}
      />

      <Box sx={styles.tilesContainer}>
        {filteredTiles.length === 0 ? (
          <NoPSEs
            selectedMenu={selectedMenu}
            statusNotificationCounts={statusNotificationCounts}
          />
        ) : (
          <Box sx={styles.tilesGrid}>
            {filteredTiles.map((tile) => (
              <ProductionStepExecutionsTile
                key={tile.objectId}
                tile={tile}
                onClick={handleTileClick}
                productionDay={productionDay}
                renderPrint={renderPrint}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  )
}

const styles = {
  tilesContainer: {
    flex: 1,
  },
  tilesGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  emptyData: {
    fontSize: "20px",
    color: COLORS.BLACK800,
    textAlign: "center",
    lineHeight: 1.4,
    fontWeight: 500,
  },
}

export default ProductionStepExecutionsTable
